import { normalizeRpcError } from "../../../utils/rpc-error.js";
// src/modules/AuthWrapper2/utils/auth-error-messages.ts
/**
* Maps a thrown RPC error to copy we are willing to show on the login screen.
*
* Server messages are never rendered. They read as system malfunctions rather than
* user feedback ("no active users found" for a wrong password), leak internals
* ("No procedure handler found for scope=auth..."), and leak account existence
* ("no active users found for email jane@example.com"). The raw text is logged
* instead, so it stays available in the console and in Sentry.
*/
var OFFLINE = "No internet connection. Check your connection and try again.";
var GENERIC_LOGIN = "Something went wrong logging in. Please try again.";
/** Codes that mean the loginPassToken is no longer usable — restart the flow. */
var SESSION_EXPIRED_CODES = /* @__PURE__ */ new Set([
	400,
	401,
	403
]);
var SESSION_EXPIRED_MESSAGE = "Your login session expired. Please sign in again.";
/**
* Whether a failure mid-login means the loginPassToken is spent, so the flow has to
* restart rather than be retried. Callers branch on this rather than on the message
* text, so rewording the copy cannot change behaviour.
*/
function isSessionExpiredError(err) {
	const { code, offline } = normalizeRpcError(err);
	return !offline && SESSION_EXPIRED_CODES.has(code);
}
function log(action, err) {
	const normalized = normalizeRpcError(err);
	console.error(`${action} failed`, normalized.code, normalized.detail, err);
	return normalized;
}
function getLoginErrorMessage(err) {
	const { code, offline } = log("login", err);
	if (offline) return OFFLINE;
	switch (code) {
		case 404: return "Incorrect email or password. Please try again.";
		case 400:
		case 422: return "Please check your email and password and try again.";
		case 401:
		case 403: return "This account can't sign in. Contact your administrator.";
		case 429: return "Too many attempts. Please wait a moment and try again.";
		default: return GENERIC_LOGIN;
	}
}
function getPickUserErrorMessage(err) {
	const { code, offline } = log("login as user", err);
	if (offline) return OFFLINE;
	if (SESSION_EXPIRED_CODES.has(code)) return SESSION_EXPIRED_MESSAGE;
	return "Couldn't sign in as that user. Please try again.";
}
function getNewPasswordErrorMessage(err) {
	const { code, offline } = log("set new password", err);
	if (offline) return OFFLINE;
	switch (code) {
		case 400: return "That password reset link has expired. Request a new one.";
		case 422: return "That password doesn't meet the requirements. Please choose another.";
		default: return "Couldn't set your new password. Please try again.";
	}
}
/**
* Password reset deliberately has no code-to-copy map. The flow reports the same
* outcome whether or not the email exists, so the only failure worth surfacing is
* one where nothing was sent at all.
*/
function getPasswordResetErrorMessage(err) {
	const { offline } = log("request password reset", err);
	return offline ? OFFLINE : "";
}
export { SESSION_EXPIRED_MESSAGE, getLoginErrorMessage, getNewPasswordErrorMessage, getPasswordResetErrorMessage, getPickUserErrorMessage, isSessionExpiredError };

//# sourceMappingURL=auth-error-messages.js.map